import useScreenBreakpoint from "@hooks/useScreenBreakpoint";
import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery, UseQueryResult } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "@selectors/enviromentSelectors";
import PersesSearchAPI from "@services/persesSearchService";
import { QUERY_STATUS } from "@utils/constants/dashboard/dashboardModuleConstants";
import { getHistogramAxisFromDateRange } from "@utils/helpers/dateHelper";
import { CaseType } from "@customTypes/components/filters";
import { SimplyfiedContact } from "@customTypes/user/contact";
import { ChartGroups, ChartSeries } from "@customTypes/chart";
import { LawsuitByCustomerResponse } from "@customTypes/services/responses";
import { ONE_HOUR_MILLISECONDS } from "@utils/constants/react-query";

const parseLawsuitByCustomerOverTimeResponse = (
  response: LawsuitByCustomerResponse = [],
  isMobileScreen: boolean
) => {
  const noData = response.length === 0;
  let xAxis = getHistogramAxisFromDateRange("LAST_YEAR");

  let data = xAxis.map((startOfMonth: string) => {
    const foundBucket = response.find((bucket) => bucket.firstKey === startOfMonth);
    return foundBucket ? foundBucket.count : 0;
  });

  if (isMobileScreen) {
    xAxis = xAxis.slice(6);
    data = data.slice(6);
  }

  const groups: ChartGroups = [];
  const years: string[] = xAxis.map((date: string) => moment(date).format("YYYY"));
  const uniqueYears = Array.from(new Set(years));

  uniqueYears.forEach((year) => {
    const cols = years.filter((y) => y === year).length;
    groups.push({ title: year, cols });
  });

  return { xAxis, series: [{ name: "customers", data }], groups, noData };
};

type LawsuitByCustomerOverTimeType = {
  xAxis: string[];
  series: ChartSeries;
  groups: ChartGroups;
  noData: boolean;
};

type UseLawsuitByCustomerOverTimeColumnChartType = {
  (
    contacts: SimplyfiedContact[],
    caseTypeFilter: CaseType[]
  ): [
    status: string,
    lawsuitByCustomerOverTime: LawsuitByCustomerOverTimeType | undefined,
    fetchedTime: number,
    refetch: UseQueryResult["refetch"],
  ];
};

const useLawsuitByCustomerOverTimeColumnChartQuery: UseLawsuitByCustomerOverTimeColumnChartType = (
  contacts = [],
  caseTypeFilter
) => {
  const [lawsuitByCustomerOverTime, setLawsuitByCustomerOverTime] =
    useState<LawsuitByCustomerOverTimeType>({
      xAxis: [],
      series: [{ name: undefined, data: [] }],
      groups: [],
      noData: false,
    });
  const [status, setStatus] = useState(QUERY_STATUS.loading);

  const { isMobileScreen } = useScreenBreakpoint();

  const fromDate = moment().subtract(11, "months").startOf("month").format("YYYY-MM-DD");
  const toDate = moment().endOf("month").format("YYYY-MM-DD");

  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const contactIds = contacts.map((contact) => contact.id.toString());

  const { data, isSuccess, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery(
    ["lawsuitByCustomerOverTime", { contactIds, caseTypeFilter, fromDate, toDate }],
    () =>
      perses
        .getLawsuitByCustomerOverTimeHistogram(contactIds, caseTypeFilter, fromDate, toDate)
        .then(({ buckets }) => buckets),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: ONE_HOUR_MILLISECONDS,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseLawsuitByCustomerOverTimeResponse(data, isMobileScreen);
      setLawsuitByCustomerOverTime(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [data, isSuccess, isFetching, isLoading, isMobileScreen]);

  if (contacts.length < 1) return [status, undefined, dataUpdatedAt, refetch];
  return [status, lawsuitByCustomerOverTime, dataUpdatedAt, refetch];
};

export default useLawsuitByCustomerOverTimeColumnChartQuery;
